import { useState, useEffect } from 'react';
import { Menu, X, Instagram } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useStore } from '../../data/store';
import RnRLogo from './RnRLogo';

const anchorLinks = [
  { label: 'LINEUP', href: '#lineup' },
  { label: 'INFOS', href: '#infos' },
  { label: 'GALERIE', href: '#gallery' },
  { label: 'KONTAKT', href: '#kontakt' },
];

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const settings = useStore((s) => s.settings);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('body-no-scroll');
    } else {
      document.body.classList.remove('body-no-scroll');
    }
    return () => document.body.classList.remove('body-no-scroll');
  }, [isOpen]);

  const handleAnchorClick = (e, href) => {
    e.preventDefault();
    setIsOpen(false);
    if (!isHome) {
      window.location.href = '/' + href;
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const navLinkClass = 'font-heading text-sm tracking-wider text-text-muted hover:text-text-primary transition-colors border-b-2 border-transparent hover:border-accent-red pb-1';
  const mobileNavLinkClass = 'font-heading text-[32px] tracking-wider text-text-primary hover:text-accent-red transition-colors py-2';

  return (
    <>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:bg-accent-red focus:text-white focus:px-4 focus:py-2">
        Zum Inhalt springen
      </a>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[rgba(15,15,15,0.95)] backdrop-blur-[10px] shadow-lg' : 'bg-transparent'
        }`}
        aria-label="Hauptnavigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <a
              href={isHome ? '#' : '/'}
              className="group flex-shrink-0"
              onClick={(e) => {
                if (isHome) { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }
              }}
              aria-label="Rock'n'Rhein – Startseite"
            >
              <RnRLogo className="h-12 md:h-14" />
            </a>

            <div className="hidden md:flex items-center gap-8">
              {anchorLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href)}
                  className={navLinkClass}
                >
                  {link.label}
                </a>
              ))}
              <Link to="/bewerben" className={navLinkClass} onClick={() => setIsOpen(false)}>
                BANDS BEWERBEN
              </Link>
              {settings?.instagram && (
                <a href={settings.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-text-muted hover:text-accent-red transition-colors">
                  <Instagram size={20} />
                </a>
              )}
            </div>

            <button
              className="md:hidden flex items-center justify-center w-11 h-11 text-text-primary"
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? 'Menü schließen' : 'Menü öffnen'}
              aria-expanded={isOpen}
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </nav>

      <div
        className={`md:hidden fixed inset-0 z-[1000] flex flex-col items-center justify-center transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ background: 'rgba(15,15,15,0.98)' }}
        aria-hidden={!isOpen}
      >
        <button
          className="absolute top-5 right-5 flex items-center justify-center w-11 h-11 text-text-primary"
          onClick={() => setIsOpen(false)}
          aria-label="Menü schließen"
        >
          <X size={32} />
        </button>
        <div className="flex flex-col items-center gap-8">
          {anchorLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleAnchorClick(e, link.href)}
              className={mobileNavLinkClass}
              tabIndex={isOpen ? 0 : -1}
            >
              {link.label}
            </a>
          ))}
          <Link
            to="/bewerben"
            className={mobileNavLinkClass}
            onClick={() => setIsOpen(false)}
            tabIndex={isOpen ? 0 : -1}
          >
            BANDS BEWERBEN
          </Link>
          {settings?.instagram && (
            <a href={settings.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-text-muted hover:text-accent-red transition-colors mt-4" tabIndex={isOpen ? 0 : -1}>
              <Instagram size={32} />
            </a>
          )}
        </div>
      </div>
    </>
  );
}
